import type { LucideIcon } from 'lucide-react';
import { FALLBACK_GUIDE_ICON, GUIDE_ICONS } from './guideIcons';
import type { HelpContext, HelpFaq } from './types';

export interface ResolvedGuideAction {
  icon: LucideIcon;
  text: string;
}

export interface ResolvedGuide {
  label: string;
  lead: string;
  quoteLead: boolean;
  actions: ResolvedGuideAction[];
  topQuestion: { id: string; question: string } | null;
}

export function firstSentence(text: string): string {
  const match = /^[\s\S]*?[.!?](?=\s|$)/.exec(text.trim());
  return (match ? match[0] : text).trim();
}

/**
 * The PageGuideCard copy for a context chain (self first, global last).
 * A section with a popover quotes that popover's first sentence as its lead and
 * inherits its page's actions; a context with no guide at all falls back to the
 * nearest ancestor's, under its own label. The chat pane is never empty.
 */
export function resolveGuide(
  chain: HelpContext[],
  topFaq: HelpFaq | null,
  popoverBodyFor: (helpKey: string) => string | undefined,
): ResolvedGuide {
  const context = chain[0];
  const inherited = chain.find((ctx) => ctx.guide?.lead);
  const popover = context.aliasOf ? popoverBodyFor(context.aliasOf) : undefined;

  let lead = '';
  let quoteLead = false;
  if (context.guide?.lead) {
    lead = context.guide.lead;
    quoteLead = context.guide.quoteLead ?? false;
  } else if (popover) {
    lead = firstSentence(popover);
    quoteLead = true;
  } else if (inherited?.guide?.lead) {
    lead = inherited.guide.lead;
  }

  const actionSource = chain.find((ctx) => ctx.guide?.actions && ctx.guide.actions.length > 0);
  const actions = (actionSource?.guide?.actions ?? []).slice(0, 3).map((action) => ({
    icon: GUIDE_ICONS[action.icon] ?? FALLBACK_GUIDE_ICON,
    text: action.text,
  }));

  return {
    label: context.label,
    lead,
    quoteLead,
    actions,
    topQuestion: topFaq ? { id: topFaq.id, question: topFaq.question } : null,
  };
}
